import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTestEvaluation } from '../hooks/useTestEvaluation';
import { 
  CheckCircle2, 
  XCircle, 
  TrendingUp, 
  BarChart3, 
  AlertCircle,
  List
} from 'lucide-react';

const TestEvaluation: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const {
    results,
    statistics,
    error,
    fetchResults,
    fetchStatistics,
    hasAccess,
    clearError,
  } = useTestEvaluation();

  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [selectedAgentVersion, setSelectedAgentVersion] = useState<string>('');
  const [dateRange, setDateRange] = useState<{ startDate: string; endDate: string }>({
    startDate: '',
    endDate: '',
  });

  // Check access on mount - only redirect if user is loaded and doesn't have access
  useEffect(() => {
    if (user && !user.roles?.includes('testContributor')) {
      navigate('/chat');
    }
  }, [user, navigate]);

  // Fetch initial data
  useEffect(() => {
    if (hasAccess) {
      fetchStatistics();
      // Fetch a sample of results to extract agent versions (max 100 for performance)
      fetchResults({ maxItems: 100 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasAccess]); // Only fetch when hasAccess changes

  // Auto-refresh statistics when filters change
  useEffect(() => {
    if (!hasAccess) return;

    const statsParams: any = {};
    
    if (selectedCategory) {
      statsParams.category = selectedCategory;
    }
    if (selectedStatus !== 'all') {
      statsParams.passed = selectedStatus === 'passed';
    }
    if (dateRange.startDate) {
      statsParams.startDate = dateRange.startDate;
    }
    if (dateRange.endDate) {
      statsParams.endDate = dateRange.endDate;
    }

    // Fetch statistics with filters
    fetchStatistics(statsParams);
    
    // Also fetch results to get agent versions (limit to 100 for performance)
    fetchResults({ ...statsParams, maxItems: 100 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory, selectedStatus, selectedAgentVersion, dateRange.startDate, dateRange.endDate, hasAccess]);

  // Get unique agent versions from statistics (requires backend support or client-side extraction)
  // Extract from results data with counts
  const agentVersions = useMemo(() => {
    const versionCounts = new Map<string, number>();
    results.forEach(r => {
      if (r.agentVersion?.versionHash && r.timestamp) {
        // Extract date from timestamp (YYYYMMDD format)
        const date = r.timestamp.substring(0, 10).replace(/-/g, '');
        const versionKey = `${date}-${r.agentVersion.versionHash}`;
        versionCounts.set(versionKey, (versionCounts.get(versionKey) || 0) + 1);
      }
    });
    return Array.from(versionCounts.entries())
      .sort((a, b) => b[0].localeCompare(a[0])) // Most recent first
      .map(([version, count]) => ({ version, count }));
  }, [results]);

  // Filter results by selected agent version (client-side)
  const filteredResults = useMemo(() => {
    if (!selectedAgentVersion) return results;
    
    return results.filter(r => {
      if (!r.agentVersion?.versionHash || !r.timestamp) return false;
      const date = r.timestamp.substring(0, 10).replace(/-/g, '');
      const versionKey = `${date}-${r.agentVersion.versionHash}`;
      return versionKey === selectedAgentVersion;
    });
  }, [results, selectedAgentVersion]);

  // Calculate statistics for display (using filtered results if agent version is selected)
  const stats = useMemo(() => {
    // If agent version is selected, calculate from filtered results
    if (selectedAgentVersion && filteredResults.length > 0) {
      const total = filteredResults.length;
      const passed = filteredResults.filter(r => r.passed).length;
      const failed = total - passed;
      const passRate = total > 0 ? (passed / total) * 100 : 0;
      const averageScore = total > 0 
        ? filteredResults.reduce((sum, r) => sum + r.overallScore, 0) / total 
        : 0;

      return {
        total,
        passed,
        failed,
        passRate,
        averageScore,
      };
    }

    // Otherwise use backend statistics
    if (!statistics) return null;

    const { summary } = statistics;
    const passRate = summary.total > 0 ? (summary.passed / summary.total) * 100 : 0;

    return {
      total: summary.total,
      passed: summary.passed,
      failed: summary.failed,
      passRate,
      averageScore: summary.averageScore,
    };
  }, [statistics, selectedAgentVersion, filteredResults]);

  // Get category breakdown (using filtered results if agent version is selected)
  const categoryBreakdown = useMemo(() => {
    // If agent version is selected, calculate from filtered results
    if (selectedAgentVersion && filteredResults.length > 0) {
      const byCategory: Record<string, { total: number; passed: number; failed: number }> = {};
      
      filteredResults.forEach(r => {
        if (r.category) {
          if (!byCategory[r.category]) {
            byCategory[r.category] = { total: 0, passed: 0, failed: 0 };
          }
          byCategory[r.category].total++;
          if (r.passed) {
            byCategory[r.category].passed++;
          } else {
            byCategory[r.category].failed++;
          }
        }
      });

      return Object.entries(byCategory)
        .map(([category, data]) => ({
          category,
          total: data.total,
          passed: data.passed,
          failed: data.failed,
          passRate: data.total > 0 ? (data.passed / data.total) * 100 : 0,
        }))
        .filter(cat => cat.total > 0)
        .sort((a, b) => b.total - a.total);
    }

    // Otherwise use backend statistics
    if (!statistics?.byCategory) return [];

    return Object.entries(statistics.byCategory)
      .map(([category, data]) => ({
        category,
        total: data.total,
        passed: data.passed,
        failed: data.failed,
        passRate: data.total > 0 ? (data.passed / data.total) * 100 : 0,
      }))
      .filter(cat => cat.total > 0) // Only show categories with results
      .sort((a, b) => b.total - a.total);
  }, [statistics, selectedAgentVersion, filteredResults]);

  const handleClearFilters = () => {
    setSelectedCategory('');
    setSelectedStatus('all');
    setSelectedAgentVersion('');
    setDateRange({ startDate: '', endDate: '' });
  };

  if (!hasAccess) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-rm-error-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-rm-ink-900 mb-2">Access Denied</h2>
          <p className="text-rm-ink-600">You need the testContributor role to access this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col space-y-6 py-6">
      {/* Header with Compact Filters */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-rm-ink-900">Test Evaluation Dashboard</h1>
          <p className="text-rm-ink-600 mt-2">Monitor agent test performance and review results</p>
        </div>
        
        {/* Compact Filters */}
        <div className="flex items-center gap-2 flex-wrap">
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="text-sm px-2 py-1 border border-rm-ink-300 rounded-md focus:outline-none focus:ring-2 focus:ring-rm-primary-500"
          >
            <option value="">All Categories</option>
            {categoryBreakdown.map((cat) => (
              <option key={cat.category} value={cat.category}>
                {cat.category} ({cat.total})
              </option>
            ))}
          </select>

          <select
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
            className="text-sm px-2 py-1 border border-rm-ink-300 rounded-md focus:outline-none focus:ring-2 focus:ring-rm-primary-500"
          >
            <option value="all">All</option>
            <option value="passed">Passed</option>
            <option value="failed">Failed</option>
          </select>

          <select
            value={selectedAgentVersion}
            onChange={(e) => setSelectedAgentVersion(e.target.value)}
            className="text-sm px-2 py-1 border border-rm-ink-300 rounded-md focus:outline-none focus:ring-2 focus:ring-rm-primary-500"
            title="Filter by agent version"
          >
            <option value="">All Versions</option>
            {agentVersions.map(({ version, count }) => (
              <option key={version} value={version}>
                {version} ({count})
              </option>
            ))}
          </select>

          <input
            type="date"
            value={dateRange.startDate}
            onChange={(e) => setDateRange({ ...dateRange, startDate: e.target.value })}
            placeholder="Start Date"
            className="text-sm px-2 py-1 border border-rm-ink-300 rounded-md focus:outline-none focus:ring-2 focus:ring-rm-primary-500"
          />

          <input
            type="date"
            value={dateRange.endDate}
            onChange={(e) => setDateRange({ ...dateRange, endDate: e.target.value })}
            placeholder="End Date"
            className="text-sm px-2 py-1 border border-rm-ink-300 rounded-md focus:outline-none focus:ring-2 focus:ring-rm-primary-500"
          />

          {(selectedCategory || selectedStatus !== 'all' || selectedAgentVersion || dateRange.startDate || dateRange.endDate) && (
            <button
              onClick={handleClearFilters}
              className="text-sm px-2 py-1 bg-rm-ink-100 text-rm-ink-700 rounded-md hover:bg-rm-ink-200 transition-colors"
            >
              Clear
            </button>
          )}

          <button
            onClick={() => navigate('/test-evaluation/browse')}
            className="flex items-center text-sm px-2 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            <List className="w-4 h-4 mr-1" />
            Browse All Results
          </button>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-rm-error-50 border border-rm-error-200 rounded-lg p-4 flex items-start">
          <AlertCircle className="w-5 h-5 text-rm-error-500 mr-3 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm text-rm-error-700">{error}</p>
          </div>
          <button
            onClick={clearError}
            className="text-rm-error-500 hover:text-rm-error-700 ml-4"
          >
            ✕
          </button>
        </div>
      )}

      {/* Statistics Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white border border-rm-ink-200 rounded-lg p-6 shadow-card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-rm-ink-600 mb-1">Total Tests</p>
                <p className="text-3xl font-bold text-rm-ink-900">{stats.total}</p>
              </div>
              <BarChart3 className="w-8 h-8 text-rm-primary-500" />
            </div>
          </div>

          <div className="bg-white border border-rm-ink-200 rounded-lg p-6 shadow-card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-rm-ink-600 mb-1">Passed</p>
                <p className="text-3xl font-bold text-rm-success-600">{stats.passed}</p>
              </div>
              <CheckCircle2 className="w-8 h-8 text-rm-success-500" />
            </div>
          </div>

          <div className="bg-white border border-rm-ink-200 rounded-lg p-6 shadow-card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-rm-ink-600 mb-1">Failed</p>
                <p className="text-3xl font-bold text-rm-error-600">{stats.failed}</p>
              </div>
              <XCircle className="w-8 h-8 text-rm-error-500" />
            </div>
          </div>

          <div className="bg-white border border-rm-ink-200 rounded-lg p-6 shadow-card">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-rm-ink-600 mb-1">Pass Rate</p>
                <p className="text-3xl font-bold text-rm-primary-600">{stats.passRate.toFixed(1)}%</p>
              </div>
              <TrendingUp className="w-8 h-8 text-rm-primary-500" />
            </div>
          </div>
        </div>
      )}

      {/* Category Performance */}
      {categoryBreakdown.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold text-rm-ink-900 mb-3">Category Performance</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2">
            {categoryBreakdown.map((cat) => (
              <div 
                key={cat.category}
                className="bg-white border border-rm-ink-200 rounded p-3 shadow-sm hover:shadow-md transition-shadow"
              >
                {/* Category Name */}
                <h3 className="text-sm font-semibold text-rm-ink-900 mb-2 truncate" title={cat.category}>
                  {cat.category}
                </h3>
                
                {/* Stats Grid */}
                <div className="grid grid-cols-2 gap-1.5 mb-2">
                  <div className="bg-rm-ink-50 rounded p-1.5">
                    <p className="text-xs text-rm-ink-600 mb-0.5">Total</p>
                    <p className="text-lg font-bold text-rm-ink-900">{cat.total}</p>
                  </div>
                  <div className="bg-rm-success-50 rounded p-1.5">
                    <p className="text-xs text-rm-success-700 mb-0.5">Passed</p>
                    <p className="text-lg font-bold text-rm-success-600">{cat.passed}</p>
                  </div>
                  <div className="bg-rm-error-50 rounded p-1.5">
                    <p className="text-xs text-rm-error-700 mb-0.5">Failed</p>
                    <p className="text-lg font-bold text-rm-error-600">{cat.failed}</p>
                  </div>
                  <div className="bg-rm-primary-50 rounded p-1.5">
                    <p className="text-xs text-rm-primary-700 mb-0.5">Pass Rate</p>
                    <p className="text-lg font-bold text-rm-primary-600">{cat.passRate.toFixed(1)}%</p>
                  </div>
                </div>
                
                {/* Progress Bar */}
                <div className="w-full bg-rm-ink-100 rounded-full h-1.5">
                  <div
                    className="bg-gradient-to-r from-rm-success-500 to-rm-success-600 h-1.5 rounded-full transition-all"
                    style={{ width: `${cat.passRate}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default TestEvaluation;
